import { ArrowLeft, Filter, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  adminClient,
  type StudioTeamActivityPage,
  type StudioTeamActivityQuery,
  type StudioTeamChange,
  type StudioTeamRoleSummary,
} from '@/api/adminClient';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { SearchInput } from '@/components/table/SearchInput';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import {
  ACTIVITY_ACTIONS,
  type RoleCatalog,
  SEARCH_MIN_CHARS,
  activityParams,
  activityQueryFrom,
  changeLineParts,
  changeTarget,
  rangeLabel,
  roleCatalog,
  userDetailPath,
} from './usersModel';

const FIELD_CLASS =
  'h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-auto sm:text-sm';

/** The person a change was made to: a link to their page once Studio Admin has one, plain text until then. */
function ChangePerson({ change }: { change: StudioTeamChange }) {
  const path = userDetailPath(change.targetUserId);
  const label = changeTarget(change);
  if (path) {
    return (
      <Link to={path} className="font-medium text-foreground underline-offset-2 hover:underline">
        {label}
      </Link>
    );
  }
  return <span className="font-medium">{label}</span>;
}

function ChangeRow({ change, catalog, onPerson }: { change: StudioTeamChange; catalog: RoleCatalog; onPerson: (person: string) => void }) {
  const [before, after] = changeLineParts(change, catalog);
  const person = change.targetUserId || change.targetEmail;
  return (
    <li className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground sm:w-40">{formatDateTime(change.at)}</span>
      <span className="min-w-0 flex-1 text-sm text-foreground">
        {before}
        <ChangePerson change={change} />
        {after}
      </span>
      {person ? (
        <button
          type="button"
          onClick={() => onPerson(person)}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1 self-start rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          aria-label={`Only changes for ${changeTarget(change)}`}
        >
          <Filter className="h-3 w-3" aria-hidden="true" />
          Only this person
        </button>
      ) : null}
    </li>
  );
}

export type UserChangesViewProps = {
  roles: StudioTeamRoleSummary[];
  query: StudioTeamActivityQuery;
  onQueryChange: (query: StudioTeamActivityQuery) => void;
  search: string;
  onSearchChange: (text: string) => void;
  result?: StudioTeamActivityPage;
  loading: boolean;
  error?: string;
  onRetry: () => void;
};

/** The change log without its data fetching, so it can render from props alone. */
export function UserChangesView({ roles, query, onQueryChange, search, onSearchChange, result, loading, error, onRetry }: UserChangesViewProps) {
  const catalog = useMemo(() => roleCatalog({ roles, roleGroups: [] }), [roles]);
  const set = (patch: Partial<StudioTeamActivityQuery>) => onQueryChange({ ...query, ...patch, page: patch.page ?? 1 });
  const filtered = Boolean(query.person || query.role || query.action || query.from || query.to || query.q);
  const personLabel = query.person && result?.entries.find((entry) => entry.targetUserId === query.person || entry.targetEmail === query.person);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <Link to="/users" className="mb-3 inline-flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Users
      </Link>
      <AdminPageHeader title="Role changes" description="Every Studio role given, removed or promised with an invitation from Studio Admin, newest first." />

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
          <SearchInput value={search} onChange={onSearchChange} placeholder={`Search people, who changed it, or role (${SEARCH_MIN_CHARS}+ letters)`} label="Search changes" />
          <select aria-label="Action" value={query.action ?? ''} onChange={(event) => set({ action: (event.target.value || undefined) as StudioTeamActivityQuery['action'] })} className={FIELD_CLASS}>
            <option value="">Any action</option>
            {ACTIVITY_ACTIONS.map((action) => (
              <option key={action.id} value={action.id}>
                {action.label}
              </option>
            ))}
          </select>
          <select aria-label="Role" value={query.role ?? ''} onChange={(event) => set({ role: event.target.value || undefined })} className={FIELD_CLASS}>
            <option value="">Any role</option>
            {roles.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label}
              </option>
            ))}
          </select>
          <label className="flex w-full items-center gap-2 text-xs text-muted-foreground sm:w-auto">
            From
            <input type="date" aria-label="From" value={query.from ?? ''} max={query.to} onChange={(event) => set({ from: event.target.value || undefined })} className={FIELD_CLASS} />
          </label>
          <label className="flex w-full items-center gap-2 text-xs text-muted-foreground sm:w-auto">
            To
            <input type="date" aria-label="To" value={query.to ?? ''} min={query.from} onChange={(event) => set({ to: event.target.value || undefined })} className={FIELD_CLASS} />
          </label>
          {query.person ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-foreground">
              Person: {personLabel ? changeTarget(personLabel) : query.person}
              <button type="button" onClick={() => set({ person: undefined })} aria-label="Show every person" className="cursor-pointer rounded-full p-0.5 text-muted-foreground hover:text-foreground">
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ) : null}
          {filtered ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onQueryChange({ page: 1 })}>
              Clear filters
            </Button>
          ) : null}
        </div>
        {loading && !result ? (
          <p className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading changes
          </p>
        ) : error && !result ? (
          <div role="alert" className="flex flex-wrap items-center gap-3 px-4 py-6 text-sm text-destructive-foreground">
            <span className="min-w-0 flex-1">{error}</span>
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : result && result.entries.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">{filtered ? 'No changes match.' : 'No changes yet.'}</p>
        ) : result ? (
          <ul className={cn('divide-y divide-border', loading && 'opacity-60')} aria-busy={loading}>
            {result.entries.map((change, index) => (
              <ChangeRow key={change.id ?? `${change.at}-${index}`} change={change} catalog={catalog} onPerson={(person) => set({ person })} />
            ))}
          </ul>
        ) : null}
        {result && result.total > 0 ? (
          <TablePager
            total={result.total}
            limit={result.pageSize}
            offset={(result.page - 1) * result.pageSize}
            onOffsetChange={(offset) => set({ page: Math.floor(offset / result.pageSize) + 1 })}
            label={rangeLabel(result.page, result.pageSize, result.total, 'changes')}
            disabled={loading}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Studio Admin → Users → Role changes: the full log, paged on the server,
 * filtered by person, role, action and dates, and searched. The filters live
 * in the address, so a filtered view can be shared.
 */
export function UserChangesPage() {
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => activityQueryFrom(params), [params]);
  const [search, setSearch] = useState(query.q ?? '');
  const debounced = useDebouncedValue(search.trim());
  const [roles, setRoles] = useState<StudioTeamRoleSummary[]>([]);
  const [result, setResult] = useState<StudioTeamActivityPage | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const request = useRef(0);

  const update = useCallback((next: StudioTeamActivityQuery) => setParams(activityParams(next), { replace: true }), [setParams]);

  const lastSearch = useRef(debounced);
  useEffect(() => {
    if (lastSearch.current === debounced) return;
    lastSearch.current = debounced;
    const q = debounced.length >= SEARCH_MIN_CHARS ? debounced : undefined;
    if (q !== query.q) update({ ...query, q, page: 1 });
  }, [debounced, query, update]);

  useEffect(() => {
    adminClient
      .getTeamAccess()
      .then((data) => setRoles(data.roles))
      .catch(() => setRoles([]));
  }, []);

  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError(undefined);
    try {
      const page = await adminClient.getTeamActivity(query);
      if (id === request.current) setResult(page);
    } catch (failure) {
      if (id === request.current) setError(failure instanceof Error ? failure.message : 'Failed to load the changes');
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <UserChangesView
      roles={roles}
      query={query}
      onQueryChange={(next) => {
        if (next.q === undefined && query.q) setSearch('');
        update(next);
      }}
      search={search}
      onSearchChange={setSearch}
      result={result}
      loading={loading}
      error={error}
      onRetry={() => void load()}
    />
  );
}
